import { useAuth0 } from '@auth0/auth0-react';
import { useParams } from 'react-router';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { HideIdentifiedProfilesRequest } from './DTO/HideIdentifiedProfilesRequest';
import { DefaultIdentifiedProfileResponse, IdentifiedProfileResponse } from './DTO/IdentifiedProfileResponse';
import { ShortlistIdentifiedProfilesRequest } from './DTO/ShortlistIdentifiedProfilesRequest';
import useAISearchCounters from './useAISearchCounters';
import useAISearchProgress from './useAISearchProgress';
import Page from '@/Models/Page';
import SortingDirection from '@/Models/SortingDirection';
import http from '@/Utils/HTTP/NewHttpUtils';

export type PossibleMatchScoreRanges = number[];

interface IdentifiedProfilesRegionPair {
    country: string;
    region: string;
}

export interface IdentifiedProfileOptions {
    search?: string;
    sortByColumn?: string;
    sortingDirection?: SortingDirection;
    matchScoreMin?: number;
    matchScoreMax?: number;
    companyId?: string[];
    country?: string[];
    regionPair?: IdentifiedProfilesRegionPair[];
}

export const identifiedListPaginationSizeOptions = [20, 50, 75, 100, 150];
export const identifiedListDefaultPageSize = identifiedListPaginationSizeOptions[2]; // 75 is default for identified list.

const buildQuery = (offset: number, limit: number): string => {
    const query = new URLSearchParams({
        offset: offset.toString(),
        limit: limit.toString(),
    });

    return query.toString();
};

export interface IdentifiedProfileFilterResponse {
    id: string;
    name: string;
    count: number;
}

export interface IdentifiedProfileRegionFilterResponse
    extends IdentifiedProfileFilterResponse, IdentifiedProfilesRegionPair {}

export interface IdentifiedProfileFiltersResponse {
    companies: IdentifiedProfileFilterResponse[];
    countries: IdentifiedProfileFilterResponse[];
    regions: IdentifiedProfileRegionFilterResponse[];
}

export const useIdentifiedProfileFilters = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { assessmentId } = useParams();
    const { data, isValidating } = useAISearchProgress();

    return useSWR(
        data?.isInProgress === true || isValidating
            ? null
            : `recruitments/${assessmentId}/ai-search/identified/filters`,
        async (path) => {
            const response = await http.get<IdentifiedProfileFiltersResponse>({
                path,
                accessToken: await getAccessTokenSilently(),
            });

            return response;
        },
    );
};

export const useIdentifiedProfiles = (limit: number, offset: number, payload: IdentifiedProfileOptions) => {
    const { getAccessTokenSilently } = useAuth0();
    const { assessmentId } = useParams();
    const { data, isValidating } = useAISearchProgress();
    const { mutate: mutateAISearchCounters } = useAISearchCounters();
    const { mutate: mutateIdentifiedProfileFilters } = useIdentifiedProfileFilters();

    // there are two conditions below that blocks the fetch of identified profiles.
    // Condition A
    // if a profile ranking process is ongoing, we block until isInProgress is false.
    // Condition B
    // if no profiles currently ranking, but a recent mutation call is made to revalidate AISearchProgress,
    // we block until backend returns the current state of AISearchProgress for this specific recruitment.
    // Eg. refine search > no matching profiles > AISearchProgress revalidating > AISearchProgress revalidated > isInProgress stays in false
    // This refetch of identified profiles list is necessary to reflect the state of the backend to the frontend when no profiles found without reloading the page.
    const url =
        data?.isInProgress === true || isValidating
            ? null
            : `recruitments/${assessmentId}/ai-search/identified/?${buildQuery(offset, limit)}`;

    return useSWR(
        url
            ? [
                  url,
                  payload.search,
                  payload.sortByColumn,
                  payload.sortingDirection,
                  payload.matchScoreMin,
                  payload.matchScoreMax,
                  payload.companyId,
                  payload.country,
                  payload.regionPair,
              ]
            : null,
        async ([path]) => {
            const response = await http.post<Page<IdentifiedProfileResponse>>({
                path,
                body: payload,
                accessToken: await getAccessTokenSilently(),
            });

            return response || DefaultIdentifiedProfileResponse;
        },
        {
            onSuccess: async () => {
                await mutateAISearchCounters();
                await mutateIdentifiedProfileFilters();
            },
        },
    );
};

export const useShortlistIdentifiedProfiles = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { assessmentId } = useParams();

    return useSWRMutation(
        `recruitments/${assessmentId}/ai-search/identified-profile/shortlist`,
        async (path: string, { arg }: { arg: ShortlistIdentifiedProfilesRequest }) => {
            await http.post({
                path,
                accessToken: await getAccessTokenSilently(),
                body: arg,
            });
        },
    );
};

export const useHideIdentifiedProfiles = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { assessmentId } = useParams();

    return useSWRMutation(
        `recruitments/${assessmentId}/ai-search/identified-profile/hide`,
        async (path: string, { arg }: { arg: HideIdentifiedProfilesRequest }) => {
            await http.post({
                path,
                accessToken: await getAccessTokenSilently(),
                body: arg,
            });
        },
    );
};
